"use client";

import { usePathname } from "next/navigation";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "@/hooks/useAlert";
import { useRouter } from "@bprogress/next";

type UnsavedDataState = {
  title: string;
  count: number;
  isSaving: boolean;
};

type UnsavedDataContextType = {
  title: string;
  count: number;
  isSaving: boolean;
  hasUnsavedData: boolean;
  resolve: () => void;
  markUnsaved: (title?: string, count?: number) => void;
  markSaved: (count?: number) => void;
  saveUnsavedData: () => Promise<void>;
  setSaveCallback: (callback: () => Promise<void | boolean>) => void;
  setDiscardCallback: (callback?: () => void | Promise<void>) => void;
  setCanSave: (canSave?: boolean | (() => boolean)) => void;
  checkBeforeAction: () => Promise<boolean>;
};

export const UnsavedDataContext = createContext<UnsavedDataContextType>({
  title: "",
  count: 0,
  isSaving: false,
  hasUnsavedData: false,
  resolve: () => {},
  markUnsaved: () => {},
  markSaved: () => {},
  saveUnsavedData: async () => {},
  setSaveCallback: () => {},
  setDiscardCallback: () => {},
  setCanSave: () => {},
  checkBeforeAction: async () => true,
});

export const useHasUnsavedData = (
  saveCallback: () => Promise<void | boolean>,
  discardCallback?: () => void | Promise<void>,
  canSave?: boolean | (() => boolean),
  enabled: boolean = true
) => {
  const context = useContext(UnsavedDataContext)!;
  const hasRegisteredRef = React.useRef(false);
  const disabledContext = React.useMemo<UnsavedDataContextType>(
    () => ({
      title: "",
      count: 0,
      isSaving: false,
      hasUnsavedData: false,
      resolve: () => {},
      markUnsaved: () => {},
      markSaved: () => {},
      saveUnsavedData: async () => {},
      setSaveCallback: () => {},
      setDiscardCallback: () => {},
      setCanSave: () => {},
      checkBeforeAction: async () => true,
    }),
    []
  );

  // If a callback is provided, set it automatically
  useEffect(() => {
    if (!enabled) {
      // Only clean up if this hook instance previously registered callbacks.
      if (hasRegisteredRef.current) {
        context.setSaveCallback(async () => {});
        context.setDiscardCallback(undefined);
        context.setCanSave(undefined);
        hasRegisteredRef.current = false;
      }
      return;
    }

    hasRegisteredRef.current = true;
    if (saveCallback) {
      context.setSaveCallback(saveCallback);
    }
    context.setDiscardCallback(discardCallback);
  }, [enabled, saveCallback, discardCallback, context]);

  // Set the canSave permission check
  useEffect(() => {
    if (!enabled) {
      return;
    }

    context.setCanSave(canSave);
  }, [enabled, canSave]);

  return enabled ? context : disabledContext;
};

const useHasUnsavedDataFunction = (): UnsavedDataContextType => {
  const { t } = useTranslation("common");
  const { setIsConfirming } = useAlert();
  const router = useRouter();
  const pathname = usePathname();

  const [state, setState] = useState<UnsavedDataState>({
    title: "",
    count: 0,
    isSaving: false,
  });

  const saveCallbackRef = useRef<(() => Promise<void | boolean>) | null>(null);
  const discardCallbackRef = useRef<(() => void | Promise<void>) | null>(null);
  const canSaveRef = useRef<boolean | (() => boolean) | undefined>(undefined);
  const pendingNavigationRef = useRef<{
    type: "push" | "replace" | "href";
    url: string;
    options?: any;
  } | null>(null);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const actionPromiseResolveRef = useRef<((value: boolean) => void) | null>(
    null
  );
  const originalPushRef = useRef<typeof router.push | null>(null);
  const originalReplaceRef = useRef<typeof router.replace | null>(null);
  const isInterceptingRef = useRef<boolean>(false);
  const stateRef = useRef<UnsavedDataState>(state);

  // Helper to resolve canSave value (can be boolean or function)
  const resolveCanSave = (): boolean => {
    const canSave = canSaveRef.current;
    if (canSave === undefined) return true; // Default to true if not set
    if (typeof canSave === "function") return canSave();
    return canSave;
  };

  // Keep state ref in sync
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Store original router methods
  useEffect(() => {
    if (!originalPushRef.current && router.push) {
      originalPushRef.current = router.push.bind(router);
    }
    if (!originalReplaceRef.current && router.replace) {
      originalReplaceRef.current = router.replace.bind(router);
    }
  }, [router]);

  // Handle action completion (navigation or generic action)
  const completeAction = React.useCallback(() => {
    // Proceed with navigation
    if (pendingNavigationRef.current) {
      const { type, url, options } = pendingNavigationRef.current;
      pendingNavigationRef.current = null;

      if (type === "href") {
        // Direct href navigation
        window.location.href = url;
      } else {
        // Router navigation
        const originalMethod =
          type === "push"
            ? originalPushRef.current
            : originalReplaceRef.current;
        if (originalMethod) {
          originalMethod(url, options);
        }
      }
    }

    // Proceed with generic action
    if (pendingActionRef.current) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      action();
    }

    // Resolve promise for checkBeforeAction
    if (actionPromiseResolveRef.current) {
      actionPromiseResolveRef.current(true);
      actionPromiseResolveRef.current = null;
    }
  }, []);

  // Handle navigation warning dialog (shared function)
  const handleNavigationWarning = React.useCallback(() => {
    const message = t("unsavedData.singleChange");

    setIsConfirming(
      true,
      "unsaved",
      message,
      async () => {
        // Save and continue
        if (saveCallbackRef.current) {
          setState((prev) => ({ ...prev, isSaving: true }));
          try {
            const result = await saveCallbackRef.current();
            if (result === false) {
              setState((prev) => ({ ...prev, isSaving: false }));
              pendingNavigationRef.current = null;
              pendingActionRef.current = null;
              if (actionPromiseResolveRef.current) {
                actionPromiseResolveRef.current(false);
                actionPromiseResolveRef.current = null;
              }
              return;
            }
            setState((prev) => ({
              ...prev,
              count: 0,
              title: "",
              isSaving: false,
            }));
          } catch (error) {
            console.error("Error saving unsaved data:", error);
            setState((prev) => ({ ...prev, isSaving: false }));
            pendingNavigationRef.current = null;
            pendingActionRef.current = null;
            if (actionPromiseResolveRef.current) {
              actionPromiseResolveRef.current(false);
              actionPromiseResolveRef.current = null;
            }
            return;
          }
        } else {
          // No save callback, just clear the state
          setState((prev) => ({
            ...prev,
            count: 0,
            title: "",
            isSaving: false,
          }));
        }

        completeAction();
      },
      async () => {
        // Continue anyway (discard changes)
        if (discardCallbackRef.current) {
          await discardCallbackRef.current();
        }
        setState((prev) => ({ ...prev, count: 0, title: "", isSaving: false }));
        completeAction();
      }
    );
  }, [t, setIsConfirming, completeAction]);

  // Intercept router.push and router.replace
  useEffect(() => {
    if (!router || isInterceptingRef.current) {
      return;
    }

    const originalPush = originalPushRef.current;
    const originalReplace = originalReplaceRef.current;

    if (!originalPush || !originalReplace) {
      return;
    }

    isInterceptingRef.current = true;

    const wrappedPush = (
      url: string,
      options?: Parameters<typeof router.push>[1]
    ) => {
      const currentState = stateRef.current;
      // Skip interception if user cannot save (no point warning about unsaved data)
      if (
        currentState.count > 0 &&
        !currentState.isSaving &&
        resolveCanSave()
      ) {
        // Store pending navigation but DON'T call originalPush
        // This prevents bgProgress from starting
        pendingNavigationRef.current = { type: "push", url, options };
        handleNavigationWarning();
        // Explicitly return to ensure originalPush is never called
        return;
      }
      // Only call originalPush if no unsaved data or user cannot save
      originalPush(url, options);
    };

    const wrappedReplace = (
      url: string,
      options?: Parameters<typeof router.replace>[1]
    ) => {
      const currentState = stateRef.current;
      // Skip interception if user cannot save (no point warning about unsaved data)
      if (
        currentState.count > 0 &&
        !currentState.isSaving &&
        resolveCanSave()
      ) {
        // Store pending navigation but DON'T call originalReplace
        // This prevents bgProgress from starting
        pendingNavigationRef.current = { type: "replace", url, options };
        handleNavigationWarning();
        // Explicitly return to ensure originalReplace is never called
        return;
      }
      // Only call originalReplace if no unsaved data or user cannot save
      originalReplace(url, options);
    };

    // Override router methods
    (router as any).push = wrappedPush;
    (router as any).replace = wrappedReplace;

    return () => {
      if (originalPush && originalReplace) {
        (router as any).push = originalPush;
        (router as any).replace = originalReplace;
      }
      isInterceptingRef.current = false;
    };
  }, [router, handleNavigationWarning]);

  // Intercept anchor tag clicks for href links
  useEffect(() => {
    const handleLinkClick = (e: MouseEvent) => {
      const currentState = stateRef.current;

      // Only intercept if we have unsaved data and user can save
      // Skip interception if user cannot save (no point warning about unsaved data)
      if (
        currentState.count === 0 ||
        currentState.isSaving ||
        !resolveCanSave()
      ) {
        return;
      }

      const target = e.target as HTMLElement;
      // Find the anchor tag (could be the clicked element or a parent)
      const anchor = target.closest("a");

      if (!anchor || !anchor.href) {
        return;
      }

      const href = anchor.getAttribute("href");
      if (!href) {
        return;
      }

      // Don't intercept hash-only links (same page anchors)
      if (href.startsWith("#")) {
        return;
      }

      // Don't intercept if link has target="_blank" or download attribute
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) {
        return;
      }

      // Check if it's a same-origin link (relative or same domain)
      try {
        const url = new URL(href, window.location.origin);
        const currentOrigin = window.location.origin;

        // External links and special protocols (mailto:, tel:, etc.) should work normally
        if (url.origin !== currentOrigin && !href.startsWith("/")) {
          return;
        }

        // Intercept ALL links (including Next.js Links) to prevent navigation
        // Next.js Links use next/navigation router which we don't intercept,
        // so we need to catch them in the click handler
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // For relative paths, use router.push to maintain client-side navigation
        // For absolute URLs, use direct href navigation
        if (href.startsWith("/") && originalPushRef.current) {
          // Use router navigation for relative paths (maintains SPA behavior)
          pendingNavigationRef.current = { type: "push", url: href };
        } else {
          // Use direct href navigation for absolute URLs
          pendingNavigationRef.current = { type: "href", url: href };
        }

        // Show confirmation modal
        handleNavigationWarning();
      } catch (error) {
        // Invalid URL, let it proceed normally
        return;
      }
    };

    // Use capture phase (true) to intercept BEFORE bgProgress starts
    // This ensures we prevent bgProgress from showing its progress bar
    document.addEventListener("click", handleLinkClick, true);

    return () => {
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [handleNavigationWarning]);

  // Clear pending navigation/action when modal is closed without confirmation
  const { isConfirming } = useAlert();
  const prevIsConfirmingRef = useRef(isConfirming.open);
  useEffect(() => {
    // If modal was open and is now closed, and we have pending navigation/action,
    // it means user closed/cancelled the modal - clear the pending items
    if (
      prevIsConfirmingRef.current &&
      !isConfirming.open &&
      (pendingNavigationRef.current || pendingActionRef.current)
    ) {
      // User cancelled - clear pending items and reject promise
      pendingNavigationRef.current = null;
      pendingActionRef.current = null;
      if (actionPromiseResolveRef.current) {
        actionPromiseResolveRef.current(false);
        actionPromiseResolveRef.current = null;
      }
    }
    prevIsConfirmingRef.current = isConfirming.open;
  }, [isConfirming.open]);

  // Handle pathname changes (browser back/forward, direct navigation)
  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    if (prevPathnameRef.current !== pathname) {
      if (
        state.count > 0 &&
        !state.isSaving &&
        pendingNavigationRef.current === null
      ) {
        // Pathname changed but we weren't expecting it (e.g., browser back button)
        // We can't easily prevent this, but we can at least warn via beforeunload
        // The beforeunload handler will catch most cases
      }
      prevPathnameRef.current = pathname;
    }
  }, [pathname, state.count, state.isSaving]);

  // Handle beforeunload event (browser close, refresh, external navigation)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Skip warning if user cannot save (no point warning about unsaved data)
      if (state.count > 0 && !state.isSaving && resolveCanSave()) {
        e.preventDefault();
        // Modern browsers ignore custom messages
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [state.count, state.isSaving]);

  const markUnsaved = (title?: string, count?: number) => {
    // Don't track unsaved data if user cannot save
    if (!resolveCanSave()) {
      return;
    }
    setState((prev) => ({
      ...prev,
      count: (prev.count || 0) + (count ?? 1),
      title: title || prev.title,
    }));
  };

  const markSaved = (count?: number) => {
    setState((prev) => {
      const nextCount = Math.max(0, (prev.count || 0) - (count ?? 1));
      return {
        ...prev,
        count: nextCount,
        // Clear the stored title once nothing is unsaved so stale titles don't leak
        // into the navigation-away confirmation dialog.
        title: nextCount === 0 ? "" : prev.title,
      };
    });
  };

  const resolve = () => {
    setState((prev) => ({
      ...prev,
      count: 0,
      title: "",
    }));
  };

  const saveUnsavedData = async () => {
    if (saveCallbackRef.current) {
      setState((prev) => ({ ...prev, isSaving: true }));
      try {
        const result = await saveCallbackRef.current();
        if (result === false) {
          setState((prev) => ({ ...prev, isSaving: false }));
          return;
        }
        setState((prev) => ({ ...prev, count: 0, title: "", isSaving: false }));
      } catch (error) {
        console.error("Error saving unsaved data:", error);
        setState((prev) => ({ ...prev, isSaving: false }));
        throw error;
      }
    }
  };

  const setSaveCallback = (callback: () => Promise<void | boolean>) => {
    saveCallbackRef.current = callback;
  };

  const setDiscardCallback = (callback?: () => void | Promise<void>): void => {
    discardCallbackRef.current = callback || null;
  };

  const setCanSave = (canSave?: boolean | (() => boolean)): void => {
    canSaveRef.current = canSave;
  };

  const checkBeforeAction = async (): Promise<boolean> => {
    const currentState = stateRef.current;
    // Skip check if user cannot save (no point warning about unsaved data)
    if (
      currentState.count === 0 ||
      currentState.isSaving ||
      !resolveCanSave()
    ) {
      return true;
    }

    return new Promise<boolean>((resolve) => {
      actionPromiseResolveRef.current = resolve;
      handleNavigationWarning();
    });
  };

  return {
    title: state.title,
    count: state.count,
    isSaving: state.isSaving,
    // hasUnsavedData is false if user cannot save (no point tracking unsaved data)
    hasUnsavedData: state.count > 0 && resolveCanSave(),
    resolve,
    markUnsaved,
    markSaved,
    saveUnsavedData,
    setSaveCallback,
    setDiscardCallback,
    setCanSave,
    checkBeforeAction,
  };
};

export const UnsavedDataContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const unsavedDataFunction = useHasUnsavedDataFunction();

  return (
    <UnsavedDataContext.Provider value={unsavedDataFunction}>
      {children}
    </UnsavedDataContext.Provider>
  );
};
